import { useCallback, useEffect, useRef, useState } from "react";
import { TranslationService } from "../../services/TranslationService";
import {
  calculatePageCount,
  setCurrentPageHelper,
  useEndpointData
} from "../../utils/TableUtils";
import { Table, TableHeader } from "../shared/Table";
import TableContext from "../task/TableContext";
import { ImportService } from "../../services/ImportService";
import { ImportFileLogRequest } from "../../entities/import/ImportFileLogRequest";
import { ImportFileLogResponse } from "../../entities/import/ImportFileLogResponse";
import {
  formatDateTimeDigits,
  formatIntizaDate
} from "../../utils/FormatUtils";
import { useHistory, useLocation } from "react-router-dom";
import { DatePickerEditor } from "../shared/components/Editors";
import SearchInput from "../shared/components/SearchInput";
import CompanyService from "../../services/CompanyService";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";

function isValidDate(fechaString: string) {
  const fecha = Date.parse(fechaString);
  return !isNaN(fecha) && new Date(fecha).toString() !== "Invalid Date";
}

export const FileLog = ({
  prevRequest
}: {
  prevRequest?: ImportFileLogRequest;
}) => {
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);

  const initialFrom = urlParams.get("from") || prevRequest?.from || "";
  const initialTo = urlParams.get("to") || prevRequest?.to || "";
  const initialFilename =
    urlParams.get("filename") || prevRequest?.filename || "";

  const [filtersSelected, setFiltersSelected] = useState({
    from: initialFrom,
    to: initialTo,
    filename: initialFilename
  });

  // Estados temporales para las fechas (se actualizan inmediatamente para mostrar el valor)
  const [tempFrom, setTempFrom] = useState(initialFrom);
  const [tempTo, setTempTo] = useState(initialTo);

  const [request, setRequest] = useState<ImportFileLogRequest>(
    prevRequest || {
      pageSize: 20,
      page: 0,
      from: initialFrom || undefined,
      to: initialTo || undefined,
      filename: initialFilename || undefined
    }
  );

  // Refs para los timeouts de debounce
  const fromTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const endpoint = useCallback(() => ImportService.fileLog(request), [request]);
  const context = useEndpointData(endpoint);
  context.currentPage = request.page;
  context.pageCount = calculatePageCount(context.response);
  context.setCurrentPage = setCurrentPageHelper(setRequest, context.pageCount);

  const headers = [
    new TableHeader("date", TranslationService.translate.Date, false, false),
    new TableHeader("file", TranslationService.translate.File, false, false),
    new TableHeader(
      "client",
      TranslationService.translate.Client,
      false,
      false
    ),
    new TableHeader(
      "invoice",
      TranslationService.translate.Invoice,
      false,
      false
    )
  ];

  // Función para aplicar el filtro de fecha después de validar
  const applyDateFilter = useCallback((name: "from" | "to", value: string) => {
    if (isValidDate(value)) {
      const formattedDate = formatIntizaDate(new Date(value));
      setFiltersSelected((prev) => ({ ...prev, [name]: formattedDate }));
      // Sincronizar el valor temporal con el valor formateado
      if (name === "from") {
        setTempFrom(formattedDate);
      } else {
        setTempTo(formattedDate);
      }
      setRequest((prev) => ({
        ...prev,
        page: 0,
        [name]: formattedDate || undefined
      }));
    } else if (value === "") {
      // Si el campo está vacío, limpiar el filtro
      setFiltersSelected((prev) => ({ ...prev, [name]: "" }));
      // Sincronizar el valor temporal
      if (name === "from") {
        setTempFrom("");
      } else {
        setTempTo("");
      }
      setRequest((prev) => ({
        ...prev,
        page: 0,
        [name]: undefined
      }));
    }
  }, []);

  // Handler para cambios en fechas con debounce
  const handleDateChange = (name: "from" | "to", value: string) => {
    // Actualizar el valor temporal inmediatamente para mostrar en el input
    if (name === "from") {
      setTempFrom(value);
    } else {
      setTempTo(value);
    }

    // Limpiar el timeout anterior
    const timeoutRef = name === "from" ? fromTimeoutRef : toTimeoutRef;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Aplicar debounce: esperar 1 segundo después de que el usuario termine de tipear
    timeoutRef.current = setTimeout(() => {
      applyDateFilter(name, value);
    }, 1000);
  };

  // Limpiar timeouts al desmontar
  useEffect(() => {
    const fromTimeout = fromTimeoutRef.current;
    const toTimeout = toTimeoutRef.current;
    return () => {
      if (fromTimeout) {
        clearTimeout(fromTimeout);
      }
      if (toTimeout) {
        clearTimeout(toTimeout);
      }
    };
  }, []);

  const handleFilterChange = (name: string, value: string) => {
    if (name === "from" || name === "to") {
      handleDateChange(name as "from" | "to", value);
    } else {
      const dataValue = value || undefined;
      const newFilters = { ...filtersSelected, [name]: dataValue || "" };
      setFiltersSelected(newFilters);
      setRequest((prev) => ({
        ...prev,
        page: 0,
        filename: newFilters.filename || undefined
      }));
    }
  };

  const handleFilenameSearch = (value: string) => {
    handleFilterChange("filename", value);
  };

  return (
    <div className="container-fluid padding">
      <div className="d-flex justify-content-between">
        <h2>{TranslationService.translate.FileImportLog}</h2>
      </div>
      <div className="mh-100 p-0">
        <div className="genericHeader">
          <div className="searcherFilterHeader">
            <div className="actionButtonsHeader">
              {CompanyService.canDo("export") && (
                <ButtonTooltipIcon
                  title={TranslationService.translate.Export}
                  icon="fa-light fa-arrow-down-to-line"
                  onClick={() => {
                    if (context.response && context.response.count) {
                      ImportService.exportFileLog(
                        {
                          from: request.from,
                          to: request.to,
                          filename: request.filename
                        },
                        context.response.count
                      );
                    }
                  }}
                  isLink={false}
                />
              )}
            </div>
            <SearchInput
              onSearch={handleFilenameSearch}
              autosearch={false}
              placeholder={
                TranslationService.translate.FileName || "Nombre de archivo"
              }
              defaultValue={filtersSelected.filename}
            />
            <DatePickerEditor
              onChange={(v) => handleDateChange("from", v)}
              defaultValue={tempFrom}
            />
            <DatePickerEditor
              onChange={(v) => handleDateChange("to", v)}
              defaultValue={tempTo}
            />
          </div>
        </div>
        <TableContext.Provider value={context}>
          <Table
            headers={headers}
            item={FileLogItem}
            stickyHeader={true}
            limitSize
          />
        </TableContext.Provider>
      </div>
    </div>
  );
};

const FileLogItem = ({ data }: { data: ImportFileLogResponse.Item }) => {
  const history = useHistory();
  const navigateToClient = () => {
    if (data.IO.personId) {
      history.push(
        `/${TranslationService.currentLanguage}/client/details?id=${data.IO.personId}`
      );
    }
  };
  return (
    <tr>
      <td>{formatDateTimeDigits(new Date(data.Date))}</td>
      <td>{data.Filename}</td>
      <td className="pointer" onClick={navigateToClient}>
        {data.IO.personName ?? "-"}
      </td>
      <td>{data.IO.referenceNumber ?? "-"}</td>
    </tr>
  );
};
